/**
 * Photo meta — the permanent data shape under every photograph surface in the
 * SIGNUP redesign (the round face, the People square, the photo QR).
 *
 * Stored on the own profile as `photoMeta` (rides profile_data — persisted,
 * recovered, and outbound with the photo via buildProfilePayload):
 *   { v, src, aspect, focalX, focalY, face: {x,y,w,h}|null, override|null,
 *     card }                             ← the card-grade copy (Plan 2), optional
 * The face box is THE one stored value both crops are cut from (avatarCrop.js).
 * All positions are 0–1 fractions of the source image. `src` is the photo URL
 * the meta was computed for: meta whose src isn't the current photo is stale
 * and reads as absent, so no photo-change path can show someone else's crop.
 *
 * NO COLOUR lives here (signup A′, CD Drop 7): a person's colour is chosen and
 * stored on the profile (people/personColour), never read from the picture.
 * Meta stored before A′ may still carry `colour` / `foot` / `top` / `ground` /
 * `light` / `kind` — they are ignored.
 *
 * NO LOGO MODE either (user ruling, 2026-10-03): a logo or drawing is treated
 * exactly like a photo — cut on its face box if one is found, else the centre.
 *
 * Spec: CARD §0 rules 2–3, §2c item 7 (face rectangle, 72% avatar fill);
 * packet answer 6 (centre 0.5/0.5). Pure — no DOM, no network. Browser capture
 * lives in capturePhotoMeta.js.
 */

import { isFreshMeta } from './keepPhotoMeta';

const PHOTO_META_VERSION = 1;

// No face found → the centre. The spec's own defined path, not an error mask.
export const CENTRE = 0.5;

/**
 * Read a profile's photo meta, resolved against its current photo.
 * Missing, stale (src ≠ photoUrl) or malformed meta → centre defaults.
 *
 * @param {{photoUrl?: string, photoMeta?: Object}} profile
 * @returns {{focalX:number, focalY:number, face:Object|null, aspect:number|null,
 *   override:Object|null, card:string|null}} — `face`
 *   null = no face found
 */
export const readPhotoMeta = (profile) => {
  const meta = profile?.photoMeta;
  if (!isFreshMeta(meta, profile?.photoUrl)) {
    return {
      focalX: CENTRE,
      focalY: CENTRE,
      face: null,
      aspect: null,
      override: null,
      card: null,
    };
  }
  return {
    focalX: isFraction(meta.focalX) ? meta.focalX : CENTRE,
    focalY: isFraction(meta.focalY) ? meta.focalY : CENTRE,
    face: isFace(meta.face) ? meta.face : null,
    aspect: meta.aspect > 0 ? meta.aspect : null,
    override: meta.override || null,
    // The card-grade copy (~1100px). Absent on every photo uploaded before it
    // existed, or when its upload failed — the card then draws photoUrl.
    card: typeof meta.card === 'string' ? meta.card : null,
  };
};

/**
 * Does the owner's current photo still need its one pass (plan § Existing
 * photos)? A named profile with a photo whose meta is stale or missing. Keyed
 * on face-meta freshness only (A′: there is no colour to compute). Cheap —
 * lets People skip loading the capture chunk.
 *
 * @param {{name?: string, photoUrl?: string, photoMeta?: Object}|null} profile
 * @returns {boolean}
 */
export const needsOwnPhotoPass = (profile) =>
  !!profile?.name && !!profile.photoUrl && !isFreshMeta(profile.photoMeta, profile.photoUrl);

/**
 * Build the stored meta for a freshly uploaded photo.
 * The focal point is the face's centre; no face → the centre.
 */
export const buildPhotoMeta = ({ src, aspect, face, card }) => {
  const validFace = isFace(face) ? face : null;
  return {
    v: PHOTO_META_VERSION,
    src,
    aspect,
    focalX: validFace ? round4(validFace.x + validFace.w / 2) : CENTRE,
    focalY: validFace ? round4(validFace.y + validFace.h / 2) : CENTRE,
    face: validFace,
    override: null,
    card: card || null,
  };
};

const isFraction = (n) => typeof n === 'number' && n >= 0 && n <= 1;
const isFace = (f) =>
  !!f && isFraction(f.x) && isFraction(f.y) && f.w > 0 && f.h > 0 && f.w <= 1 && f.h <= 1;
export const round4 = (n) => Math.round(n * 10000) / 10000;
